import { useEffect, useRef, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Copy, Heart, Loader2, ShieldCheck, XCircle } from "lucide-react";
import { apiClient, checkoutStatus } from "../lib/api";

const PAYMENTS = [
    { label: "Cash App", value: "$Sugarsweetsociety1L", note: "Send subtotal as a note with order #", link: "https://cash.app/$Sugarsweetsociety1L" },
    { label: "PayPal", value: "Laylah@sugarsweetsociety.com", note: "Friends & Family preferred" },
    { label: "Apple Pay", value: "Laylah@sugarsweetsociety.com", note: "Send via Messages" },
    { label: "Cash", value: "On delivery", note: "Exact amount appreciated" },
];

const MAX_POLLS = 6;
const POLL_INTERVAL = 2000;

export default function OrderConfirmation() {
    const { id } = useParams();
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [order, setOrder] = useState(null);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState("");
    const [payState, setPayState] = useState(sessionId ? "checking" : "idle"); // idle | checking | paid | failed | timeout
    const pollsRef = useRef(0);

    const fetchOrder = async () => {
        try {
            const { data } = await apiClient.get(`/orders/${id}`);
            setOrder(data);
            return data;
        } catch (e) {
            setError(e?.message || "Order not found");
            return null;
        }
    };

    useEffect(() => {
        fetchOrder();
    }, [id]);

    useEffect(() => {
        if (!sessionId) return;
        let cancelled = false;
        const poll = async () => {
            if (cancelled) return;
            pollsRef.current += 1;
            try {
                const status = await checkoutStatus(sessionId);
                if (cancelled) return;
                if (status.payment_status === "paid") {
                    setPayState("paid");
                    fetchOrder();
                    return;
                }
                if (status.status === "expired") {
                    setPayState("failed");
                    return;
                }
                if (pollsRef.current >= MAX_POLLS) {
                    setPayState("timeout");
                    return;
                }
                setTimeout(poll, POLL_INTERVAL);
            } catch {
                if (pollsRef.current >= MAX_POLLS) {
                    setPayState("timeout");
                    return;
                }
                setTimeout(poll, POLL_INTERVAL);
            }
        };
        poll();
        return () => { cancelled = true; };
    }, [sessionId]);

    const copy = async (text, key) => {
        try { await navigator.clipboard?.writeText(text); } catch { /* ignore */ }
        setCopied(key);
        setTimeout(() => setCopied(""), 1500);
    };

    if (error) {
        return <div className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="order-not-found"><p>Order not found.</p></div>;
    }

    if (!order) {
        return <div className="max-w-xl mx-auto px-4 py-20 text-center"><p className="text-[var(--sss-text-muted)]">Loading your confirmation…</p></div>;
    }

    const isCard = order.payment_method === "card";
    const isPaid = order.payment_status === "paid" || payState === "paid";

    return (
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-14" data-testid="order-confirmation-page">
            <div className="text-center">
                <div className="mx-auto w-16 h-16 rounded-full bg-[var(--sss-blush)] flex items-center justify-center mb-3">
                    <CheckCircle2 size={32} className="text-[var(--sss-pink)]" strokeWidth={1.5} />
                </div>
                <h1 className="font-serif-display text-4xl md:text-5xl font-bold">Thank you, {order.customer_name.split(" ")[0]}!</h1>
                <p className="mt-2 text-[var(--sss-text-muted)]">Your order has been received.</p>
                <p className="mt-1 text-xs text-[var(--sss-text-muted)]">Order #<span className="font-mono" data-testid="order-id">{order.id}</span></p>

                {isCard && (
                    <div className="mt-4 inline-flex items-center gap-2" data-testid="payment-status-badge">
                        {payState === "checking" && (
                            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold">
                                <Loader2 size={14} className="animate-spin" /> Verifying payment…
                            </span>
                        )}
                        {isPaid && (
                            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold">
                                <ShieldCheck size={14} /> Payment received
                            </span>
                        )}
                        {payState === "failed" && (
                            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-red-50 text-red-700 text-xs font-semibold">
                                <XCircle size={14} /> Payment expired or failed
                            </span>
                        )}
                        {payState === "timeout" && (
                            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-50 text-slate-700 text-xs font-semibold">
                                <Loader2 size={14} /> Still processing — we&apos;ll email you the moment it clears
                            </span>
                        )}
                    </div>
                )}
            </div>

            <div className="mt-10 bg-white rounded-3xl sss-shadow p-6 md:p-8">
                <h2 className="font-serif-display text-2xl font-semibold">Order Summary</h2>
                <ul className="mt-4 divide-y divide-[var(--sss-border)]">
                    {order.items.map((it, idx) => (
                        <li key={idx} className="py-3 flex justify-between gap-3 text-sm">
                            <div>
                                <p className="font-medium">{it.product_name}</p>
                                <p className="text-xs text-[var(--sss-text-muted)]">{it.size} × {it.quantity}</p>
                            </div>
                            <p className="font-serif-display font-semibold">${it.line_total.toFixed(2)}</p>
                        </li>
                    ))}
                </ul>
                <div className="border-t border-[var(--sss-border)] pt-4 mt-4 flex justify-between items-center">
                    <span className="font-medium">{isPaid ? "Total Paid" : "Total Due"}</span>
                    <span className="font-serif-display text-2xl font-bold" data-testid="confirmation-total">${order.subtotal.toFixed(2)}</span>
                </div>
            </div>

            {!isPaid && (
                <div className="mt-8 bg-[var(--sss-blush)] rounded-3xl p-6 md:p-8" data-testid="payment-instructions">
                    <h2 className="font-serif-display text-2xl font-semibold">{isCard ? "Finish your card payment" : "How to Pay"}</h2>
                    <p className="text-sm text-[var(--sss-text-muted)] mt-1">
                        {isCard
                            ? "If you didn't complete the card payment, you can still pay using any option below — include your order # in the note."
                            : <>Send <span className="font-semibold text-[var(--sss-text)]">${order.subtotal.toFixed(2)}</span> via any option below and include your order # in the note.</>
                        }
                    </p>
                    <div className="mt-5 grid sm:grid-cols-2 gap-3">
                        {PAYMENTS.map((p) => (
                            <div key={p.label} className="bg-white rounded-2xl p-4 flex items-start justify-between gap-3" data-testid={`pay-option-${p.label.toLowerCase().replace(/\s+/g, "-")}`}>
                                <div className="min-w-0">
                                    <p className="text-xs uppercase tracking-wider font-semibold text-[var(--sss-pink)]">{p.label}</p>
                                    {p.link ? (
                                        <a href={p.link} target="_blank" rel="noreferrer" className="font-medium text-sm mt-1 truncate block underline text-[var(--sss-pink)]" data-testid="pay-cashapp-link">{p.value}</a>
                                    ) : (
                                        <p className="font-medium text-sm mt-1 truncate">{p.value}</p>
                                    )}
                                    <p className="text-xs text-[var(--sss-text-muted)] mt-1">{p.note}</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => copy(p.value, p.label)}
                                    className="p-2 rounded-full hover:bg-[var(--sss-blush)] text-[var(--sss-pink)]"
                                    aria-label={`Copy ${p.label}`}
                                    data-testid={`copy-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    <Copy size={16} />
                                </button>
                            </div>
                        ))}
                    </div>
                    {copied && (
                        <p className="mt-3 text-xs text-[var(--sss-pink)] font-medium" data-testid="copy-feedback">Copied {copied}!</p>
                    )}
                    <p className="text-xs text-[var(--sss-text-muted)] mt-5">
                        Questions? Email <a href="mailto:Laylah@sugarsweetsociety.com" className="underline">Laylah@sugarsweetsociety.com</a>
                    </p>
                </div>
            )}

            {isPaid && (
                <div className="mt-8 bg-emerald-50 border border-emerald-200 rounded-3xl p-6 md:p-8 text-center" data-testid="paid-message">
                    <ShieldCheck size={28} className="mx-auto text-emerald-600" />
                    <p className="mt-2 font-serif-display text-xl font-semibold text-emerald-900">You&apos;re all set!</p>
                    <p className="mt-1 text-sm text-emerald-800">We&apos;ll be in touch shortly with delivery details.</p>
                </div>
            )}

            <div className="text-center mt-10">
                <Link to="/menu" className="btn-outline-pink" data-testid="confirmation-back-to-menu">Back to Menu</Link>
                <Link to="/" className="btn-pink ml-3" data-testid="confirmation-home">Home <Heart size={14} fill="#fff" strokeWidth={0} /></Link>
            </div>
        </div>
    );
}
